import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, NoteBlock, TableBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Risk › Screening hits (`rk-screening`), from `GET /v1/admin/screening/hits`,
 * with one subject's trail opening from a row.
 *
 * # The endpoint was built because there was nowhere to see this at all
 *
 * > *"there is no screening table, `onboarding.Screen` has no caller either.
 * > So every diligence decision in the database carries
 * > `applicant_pep_screened: false` and `applicant_sanctions_screened: false`,
 * > meaning UNKNOWN and not CLEAR, and there was no surface on which an
 * > operator could see that or do anything about it."*
 *
 * # `unconfirmed` is not `clear`, and this screen may never let it read that way
 *
 * Every row says `outcome: unconfirmed` — the vendor screened, and this build
 * cannot read what they found. Rendering that as a pass would tell a compliance
 * officer somebody was cleared when nobody knows. `onboarding.Screen` has
 * refused to read absent as clear since #40; this refuses the same thing two
 * layers up.
 *
 * # An empty queue does not mean a clean book
 *
 * The designed empty state says *"an empty queue means every alert has a
 * disposition"*, and that sentence is **false here**: nothing re-screens the
 * accepted book, and empty means no customer has reached a terminal vendor
 * verdict yet. *"That is exactly the misreading that gets an operator closed
 * down."*
 *
 * # Four designed columns cannot be served, and are named rather than drawn
 *
 * Matched name, watchlist, match score, and facets over all three. The vendor's
 * AML payload shape is not confirmed in writing, so nothing may be invented
 * into a column: *"a 'Matched name' column filled from a guessed JSON key would
 * put a sanctions name in front of a compliance officer on the strength of an
 * assumption, which is the one failure mode this screen cannot afford."*
 *
 * # Two fields that must be READ and never derived
 *
 * `open` is not `disposition === 'open'`. The table is append-only, so version
 * 1 keeps saying `open` for ever and the adjudication that resolved it is a
 * different row — the derivation is wrong in a way that looks right.
 *
 * `truncated` is not `hits.length === limit`. That would be a second
 * implementation of the bound, and *"an officer who works a list to its end and
 * sees no more rows concludes there are no more rows, and on this queue that
 * conclusion is the one that must never be reached by accident."*
 *
 * # No control, and the absence is structural
 *
 * A disposition escalates or clears a person, so it goes through the
 * authorisation seam with its own `decision_ref`. The read seam has two methods
 * and neither writes.
 */

export const SCREENING_HITS_PATH = '/screening/hits'

export function screeningTrailPath(provider: string, providerRef: string): string {
  return `/screening/subjects/${encodeURIComponent(provider)}/${encodeURIComponent(providerRef)}`
}

const SOURCE = 'postgres · screening_hits (GET /v1/admin/screening/hits)'
const AUTHORITY = 'operational'

interface Hit {
  readonly id: number | null
  readonly provider: string
  readonly providerRef: string
  readonly subjectRef: string
  /** Absent when the row was never placed against a customer. Never zero. */
  readonly customerId: number | null
  readonly source: string
  readonly outcome: string
  readonly disposition: string
  /** SERVED. Never derived from `disposition` — see the module header. */
  readonly open: boolean | null
  readonly version: number | null
  readonly supersededById: number | null
  readonly decisionRef: string
  readonly reasonCode: string
  readonly disposedBy: string
  readonly observedAt: string
  readonly recordedAt: string
}

function readHit(raw: unknown): Hit | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const provider = str(o['provider'])
  const providerRef = str(o['provider_ref'])
  if (provider === '' || providerRef === '') return null
  return {
    id: int(o['id']),
    provider,
    providerRef,
    subjectRef: str(o['subject_ref']),
    customerId: int(o['customer_id']),
    source: str(o['source']),
    outcome: str(o['outcome']),
    disposition: str(o['disposition']),
    open: typeof o['open'] === 'boolean' ? o['open'] : null,
    version: int(o['version']),
    supersededById: int(o['superseded_by_id']),
    decisionRef: str(o['decision_ref']),
    reasonCode: str(o['reason_code']),
    disposedBy: str(o['disposed_by']),
    observedAt: str(o['observed_at']),
    recordedAt: str(o['recorded_at']),
  }
}

const COLS: readonly Column[] = [
  ['Subject', 'm', 'minmax(170px,1.2fr)'],
  ['Customer', 'm', '110px'],
  ['Vendor', 'm', '130px'],
  ['What they found', 'c', '190px'],
  ['Disposition', 'c', '170px'],
  ['Still open', 'c', '130px'],
  ['Version', 'm', '100px'],
  ['Decided by', 'x', 'minmax(140px,1fr)'],
  ['Observed', 'm', '130px'],
]

function row(h: Hit): readonly Cell[] {
  return [
    h.subjectRef === '' ? h.providerRef : h.subjectRef,
    // Absent, not zero. "Customer 0" would send somebody looking for a customer
    // that is not missing — it was never resolved.
    h.customerId === null ? '— never placed' : String(h.customerId),
    h.provider === '' ? '— not stated' : h.provider,
    outcomeCell(h),
    dispositionCell(h),
    openCell(h),
    h.version === null ? '—' : `v${String(h.version)}`,
    decidedBy(h),
    h.observedAt === '' ? '—' : day(h.observedAt),
  ]
}

/**
 * What the vendor found, and the one rendering this screen may never produce.
 *
 * `unconfirmed` is tone `a` and says what it means. It is never `g`, never a
 * tick, and never the word "clear" — the vendor screened and this build cannot
 * read the result, which is a different fact from a clean screen and leads
 * somewhere else entirely.
 */
function outcomeCell(h: Hit): ChipCell {
  if (h.outcome === '') return ['— not stated', 'r']
  if (h.outcome === 'unconfirmed') return ['unconfirmed — NOT clear', 'a']
  return [h.outcome, 'n']
}

function dispositionCell(h: Hit): ChipCell {
  switch (h.disposition) {
    case '': return ['— not stated', 'r']
    case 'open': return ['open', 'a']
    case 'escalated': return ['escalated', 'r']
    case 'false_positive': return ['false positive', 'g']
    case 'confirmed': return ['confirmed', 'r']
    default: return [h.disposition, 'n']
  }
}

/**
 * `open`, from the served field.
 *
 * Deriving it from the disposition is wrong on an append-only table: version 1
 * keeps saying `open` for ever, and the row that adjudicated it is a different
 * row. A screen that derived it would show every historical version of a
 * resolved subject as still open.
 */
function openCell(h: Hit): ChipCell {
  if (h.open === null) return ['— not stated', 'r']
  return h.open ? ['open', 'a'] : ['adjudicated', 'n']
}

function decidedBy(h: Hit): Cell {
  if (h.disposedBy === '') return h.open === false ? '— not attributed' : 'nobody yet'
  return `${h.disposedBy}${h.reasonCode === '' ? '' : ` · ${h.reasonCode}`}`
}

/** The four notes the response requires, rendered as the backend wrote them. */
const REQUIRED_NOTES: readonly (readonly [key: string, kicker: string, tone: 'a' | 'n'])[] = [
  ['outcome_note', 'What the vendor found', 'a'],
  ['coverage_note', 'What an empty queue means', 'a'],
  ['column_note', 'Columns this build cannot fill', 'n'],
  ['control_note', 'Read only', 'n'],
]

function truncationNote(truncated: boolean, shown: number): NoteBlock {
  return truncated
    ? {
        t: 'note',
        tone: 'r',
        kicker: 'Not the whole queue',
        title: `This page is cut short — ${String(shown)} row(s) shown, and there are more`,
        body: 'The backend says so; this console does not infer it from the page being full, because that would be a second implementation of the bound and the two would disagree eventually. Working this list to its end and finding no more rows would otherwise read as there being no more rows, and on a screening queue that is the conclusion which must never be reached by accident.',
        ref: 'truncated is served on every response, true or false',
      }
    : {
        t: 'note',
        tone: 'n',
        kicker: 'Whole queue',
        title: 'This is every row, not the first page of them',
        body: 'The backend states that this page is the complete answer to the query. That is a served fact rather than an inference from the row count.',
        ref: 'truncated: false',
      }
}

function requiredNote(required: readonly string[]): NoteBlock {
  return {
    t: 'note',
    tone: 'n',
    kicker: 'Coverage',
    title: `Every subject is supposed to be screened against ${String(required.length)} list(s)`,
    body: `${required.join(', ')} — named by the backend rather than written down here, so this console is not carrying its own copy of somebody else's list. A subject with rows for fewer of these than named is not covered, whatever those rows say.`,
    ref: 'required_screenings comes from the response',
  }
}

export function composeScreeningHits(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['hits']) ? (payload['hits'] as readonly unknown[]) : []
  const rows: Hit[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readHit(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const truncated = payload['truncated'] === true
  const required = strings(payload['required_screenings'])

  const table: TableBlock = {
    t: 'table',
    title: 'Screening hits',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} row(s)${truncated ? ' — and more beyond this page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'A subject appears once per version. An adjudication is a new row rather than a change to an old one, so the same subject reading `open` at v1 and `escalated` at v2 is one subject that was dealt with, not two alerts.',
  }

  const blocks: Block[] = [table, truncationNote(truncated, rows.length)]

  for (const [key, kicker, tone] of REQUIRED_NOTES) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone, kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  if (required.length > 0) blocks.push(requiredNote(required))

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} screening row(s) could not be read`,
      body: 'Counted rather than dropped silently. A row without a provider and a provider reference cannot be opened into its trail, and a screening queue quietly one row shorter than the record is the failure everything else on this screen is arranged to prevent.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Empty',
      title: 'Nothing is on this queue, and that does NOT mean the book is clean',
      body: 'No customer has reached a terminal vendor verdict yet. Nothing re-screens the accepted book, so an empty queue here is not a statement that everybody has been checked and cleared — it is a statement that there is nothing to show. Reading it the other way is the misreading this screen exists to prevent.',
      ref: 'an empty queue is not a clean book',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (truncated) missing.push('the rest of the queue beyond this page')
  if (rows.some((h) => h.open === null)) missing.push('whether at least one hit is still open')
  // Always. These four are designed columns nothing in this build can fill.
  missing.push('matched name, watchlist, match score — the vendor payload shape is not confirmed')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return {
    blocks,
    provenance,
    // What each row IS, so selecting one opens that subject's trail.
    rowKeys: rows.map((h) => `${h.provider}@${h.providerRef}`),
  }
}

export const loadScreeningHits: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(SCREENING_HITS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The screening endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The screening endpoint returned a shape this console does not know.', null)
  }
  return composeScreeningHits(payload as Record<string, unknown>, new Date())
}

// ---------------------------------------------------------------------------
// One subject's trail
// ---------------------------------------------------------------------------

const TRAIL_COLS: readonly Column[] = [
  ['Version', 'm', '100px'],
  ['What they found', 'c', '190px'],
  ['Disposition', 'c', '170px'],
  ['Decided by', 'x', 'minmax(150px,1fr)'],
  ['Decision', 'm', 'minmax(170px,1.1fr)'],
  ['Observed', 'm', '130px'],
  ['Superseded by', 'm', '130px'],
]

/**
 * Every version of one subject's screening, in the order the backend returned.
 *
 * Never re-sorted. The trail is append-only and its order is the record; a
 * console that sorted it would make a trail that arrived out of order look
 * correct, and would have hidden the one fact worth reporting about an
 * append-only table.
 */
export function composeScreeningTrail(
  payload: Record<string, unknown>, readAt: Date,
): ScreenData {
  const raw = Array.isArray(payload['versions']) ? (payload['versions'] as readonly unknown[]) : []
  const rows: Hit[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readHit(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const provider = str(payload['provider'])
  const ref = str(payload['provider_ref'])

  const blocks: Block[] = [{
    t: 'table',
    title: `Screening trail — ${provider === '' ? 'unnamed vendor' : provider} · ${ref === '' ? 'no reference' : ref}`,
    src: 'ledger',
    facets: false,
    cols: TRAIL_COLS,
    rows: rows.map((h) => [
      h.version === null ? '—' : `v${String(h.version)}`,
      outcomeCell(h),
      dispositionCell(h),
      h.disposedBy === '' ? 'nobody' : h.disposedBy,
      // Law 13: the reference travels as itself, quotable without the prose.
      h.decisionRef === '' ? '— none' : h.decisionRef,
      h.observedAt === '' ? '—' : day(h.observedAt),
      h.supersededById === null ? 'current' : `#${String(h.supersededById)}`,
    ] as readonly Cell[]),
    footer: `${String(rows.length)} version(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'In the order the backend returned it, never re-sorted: the trail is append-only and its order is the record. An adjudication supersedes the version it answered, and both stay.',
  }]

  for (const [key, kicker, tone] of [
    ['history_note', 'History', 'n'],
    ['column_note', 'Columns this build cannot fill', 'n'],
    ['control_note', 'Read only', 'n'],
  ] as const) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone, kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable version(s)`)
  missing.push('matched name, watchlist, match score — the vendor payload shape is not confirmed')

  return {
    blocks,
    provenance: {
      kind: 'partial',
      source: `postgres · screening_hits (GET ${screeningTrailPath(provider, ref)})`,
      authority: AUTHORITY,
      readAt: readAtStamp(readAt),
      missing,
    },
  }
}

/**
 * One subject's trail, opened from a queue row keyed `provider@provider_ref`.
 *
 * # Split on the FIRST `@`, and the reason is asymmetric
 *
 * A provider name never contains one; a provider reference may — vendors hand
 * back session ids that look like email addresses. So everything before the
 * first `@` is the vendor and everything after it is the reference, however
 * many more the reference contains.
 *
 * Splitting on the LAST one is the plausible-looking mistake: for
 * `vendor-a@user@example.com` it yields the vendor `vendor-a@user` and the
 * reference `example.com`, and the resulting request 404s against a subject
 * that does not exist. It was written that way here first, with a comment
 * arguing for the opposite of what the code did.
 */
export async function readScreeningTrail(api: ApiClient, key: string): Promise<ScreenData> {
  const at = key.indexOf('@')
  const provider = at === -1 ? '' : key.slice(0, at)
  const ref = at === -1 ? '' : key.slice(at + 1)
  if (provider === '' || ref === '') {
    const reason = 'No vendor and reference was given, so no screening trail was looked up.'
    return unreadableScreen(reason, null)
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(screeningTrailPath(provider, ref))
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The screening trail answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The screening trail returned a shape this console does not know.', null)
  }
  return composeScreeningTrail(payload as Record<string, unknown>, new Date())
}

// ---------------------------------------------------------------------------

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The screening queue could not be read',
      body: `${reason} Nothing is drawn. In particular this is NOT an empty queue: whether anybody is flagged is unknown, and an unread screening queue and a clear one are the two answers that must never be confused.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function strings(v: unknown): readonly string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
